import { SuccessCheckIcon } from "@/app/icons/success-check"
import { Trans } from "@lingui/react/macro"
import { Button } from "@rarible_int/anaconda_ui_components"
import { Loader2 } from "lucide-react"
import type { GachaBuybackOfferStatus } from "./gacha-buyback-offer-state"
import { GachaProgressLabel } from "./gacha-progress-label"

export function GachaBuybackResultSummary({
  buybackOffer,
  marketValue,
  offerStatus,
}: {
  buybackOffer: string
  marketValue: string
  offerStatus?: GachaBuybackOfferStatus
}) {
  const loading = offerStatus === "loading" || offerStatus === "preparing"
  return (
    <div className="flex flex-col gap-3 font-mono">
      <div className="text-sm text-foreground/60">Buyback Offer</div>
      <div
        className="font-headline text-2xl text-foreground leading-none tracking-tight"
        aria-busy={loading}
        data-testid="gacha-result-buyback-price"
      >
        {loading ? (
          <>
            <span
              aria-hidden="true"
              className="inline-block h-7 w-28 animate-pulse rounded bg-foreground/10"
            />
            <span className="sr-only">
              <Trans>Getting your buyback offer…</Trans>
            </span>
          </>
        ) : offerStatus === "ineligible" ? (
          <Trans>Buyback unavailable</Trans>
        ) : offerStatus === "error" ? (
          <Trans>Offer unavailable</Trans>
        ) : (
          buybackOffer
        )}
      </div>
      <div className="text-sm text-foreground/60">
        <span>Market Value </span>
        <span className="tabular-nums text-foreground/80">{marketValue}</span>
      </div>
    </div>
  )
}

type GachaBuybackResultActionsProps = {
  boughtBackLabel?: string
  buybackOffer: string
  buybackBusy: boolean
  buybackBusyLabel: string
  buybackProgressDots: string
  canAcceptBuyback: boolean
  requiresQuote?: boolean
  countdownLabel: string
  isBuybackExpired: boolean
  offerStatus?: GachaBuybackOfferStatus
  offerSlow?: boolean
  onRetryOffer?: () => void
  onOpenAnotherPack: () => void
  onRequestBuyback: () => void
}

export function GachaBuybackResultActions({
  boughtBackLabel,
  buybackOffer,
  buybackBusy,
  buybackBusyLabel,
  buybackProgressDots,
  canAcceptBuyback,
  countdownLabel,
  requiresQuote = false,
  isBuybackExpired,
  offerStatus,
  offerSlow = false,
  onRetryOffer,
  onOpenAnotherPack,
  onRequestBuyback,
}: GachaBuybackResultActionsProps) {
  const isBuybackComplete = Boolean(boughtBackLabel)
  const loading = offerStatus === "loading" || offerStatus === "preparing"
  const retry = offerStatus === "error"
  const ready = offerStatus === undefined || offerStatus === "ready"
  const buybackButtonLabel =
    offerStatus === "preparing" ? (
      <Trans>Preparing offer…</Trans>
    ) : offerStatus === "loading" ? (
      <Trans>Getting offer…</Trans>
    ) : retry ? (
      <Trans>Retry offer</Trans>
    ) : offerStatus === "ineligible" ? (
      <Trans>Buyback unavailable</Trans>
    ) : (
      <Trans>Accept Buyback {buybackOffer}</Trans>
    )

  return (
    <>
      {isBuybackComplete ? (
        <div
          className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2.5"
          data-testid="gacha-buyback-complete-notice"
        >
          <p className="flex items-center gap-1.5 font-headline text-sm text-foreground">
            <span className="flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
              <SuccessCheckIcon />
            </span>
            <Trans>Buyback complete</Trans>
          </p>
          <p className="mt-1 text-sm leading-5 text-foreground/80">
            <Trans>
              {boughtBackLabel} payout is on its way to your account.
            </Trans>
          </p>
        </div>
      ) : null}

      <div
        className={`flex flex-col gap-3 pt-1${isBuybackComplete ? "" : " sm:flex-row"}`}
        data-testid="gacha-result-buyback-actions"
      >
        <Button
          type="button"
          variant={isBuybackComplete ? "default" : "outline"}
          className="h-auto min-h-9 w-full flex-1 px-3 py-2.5 sm:min-w-0"
          onClick={onOpenAnotherPack}
        >
          <Trans>Crack Another Pack</Trans>
        </Button>
        {isBuybackComplete ? null : (
          <Button
            type="button"
            variant="default"
            className="h-auto min-h-9 w-full flex-1 whitespace-normal px-3 py-2.5 text-center text-sm leading-snug sm:min-w-0"
            disabled={
              buybackBusy ||
              (retry ? !onRetryOffer : !ready || !canAcceptBuyback)
            }
            onClick={retry ? onRetryOffer : onRequestBuyback}
          >
            {loading && (
              <Loader2
                className="mr-1.5 h-4 w-4 shrink-0 animate-spin"
                aria-hidden="true"
              />
            )}
            <GachaProgressLabel
              label={
                buybackBusy
                  ? buybackBusyLabel || buybackButtonLabel
                  : buybackButtonLabel
              }
              dots={buybackBusy ? buybackProgressDots : undefined}
            />
          </Button>
        )}
      </div>

      {isBuybackComplete ? null : (
        <p
          className="text-center font-mono text-sm text-foreground/60 tabular-nums sm:text-left"
          data-testid="gacha-result-buyback-status"
          role={offerStatus && offerStatus !== "ready" ? "status" : undefined}
        >
          {loading && offerSlow ? (
            <Trans>
              Your offer is taking a little longer. You can keep this screen
              open or check Items later.
            </Trans>
          ) : offerStatus === "preparing" ? (
            <Trans>Preparing your buyback offer…</Trans>
          ) : offerStatus === "loading" ? (
            <Trans>Getting your buyback offer…</Trans>
          ) : retry ? (
            <Trans>
              We couldn’t load your buyback offer. Please try again.
            </Trans>
          ) : offerStatus === "ineligible" ? (
            <Trans>Buyback is not available for this card.</Trans>
          ) : requiresQuote ? (
            <Trans>
              A current EVM quote is required to check buyback eligibility.
            </Trans>
          ) : isBuybackExpired ? (
            <Trans>Expired</Trans>
          ) : (
            <Trans>Buyback Expires in {countdownLabel}</Trans>
          )}
        </p>
      )}
    </>
  )
}
